import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { API_BASE } from "../lib/apiBase";

const BACKEND_URL = API_BASE;

// Send cookies on every axios call by default
axios.defaults.withCredentials = true;

// ----------------------------------------------------------------------
// Global 401 interceptor: on expired access_token, call /auth/refresh ONCE
// and retry the original request transparently. Prevents unhandled runtime
// errors like "Request failed with status code 401" when the JWT expires
// mid-session while the chat is open.
// ----------------------------------------------------------------------
let isRefreshing = false;
let refreshWaiters = [];

const subscribeRefresh = (cb) => {
  refreshWaiters.push(cb);
};
const onRefreshed = (ok) => {
  refreshWaiters.forEach((cb) => cb(ok));
  refreshWaiters = [];
};

axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only auto-refresh on 401s, only once per request, and never on the
    // auth endpoints themselves (AuthContext handles /me/login/logout/refresh
    // with its own 401 logic — intercepting them here would cause loops and
    // would redirect public-page visitors to /login).
    if (
      status === 401 &&
      original &&
      !original._retry &&
      !String(original.url || "").includes("/api/auth/refresh") &&
      !String(original.url || "").includes("/api/auth/login") &&
      !String(original.url || "").includes("/api/auth/logout") &&
      !String(original.url || "").includes("/api/auth/me")
    ) {
      original._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await axios.post(`${BACKEND_URL}/api/auth/refresh`, {});
          isRefreshing = false;
          onRefreshed(true);
        } catch (refreshErr) {
          isRefreshing = false;
          onRefreshed(false);
          // Refresh failed. Only redirect to /login if we're currently on a
          // route that genuinely requires auth. Never boot users off public
          // pages (landing, legal docs, etc.) just because an authenticated
          // API call happened to fail in the background.
          try {
            const PRIVATE_PREFIXES = ["/chat", "/admin"];
            const path =
              typeof window !== "undefined" ? window.location.pathname : "";
            const isPrivate = PRIVATE_PREFIXES.some((p) => path.startsWith(p));
            if (isPrivate && path !== "/login" && path !== "/register") {
              window.location.assign("/login?expired=1");
              return new Promise(() => {}); // never resolves
            }
          } catch (_) {
            /* ignore */
          }
          return Promise.reject(error);
        }
      }

      return new Promise((resolve, _reject) => {
        subscribeRefresh((ok) => {
          if (ok) {
            resolve(axios(original));
          } else {
            // Refresh failed — the refresher has already triggered redirect.
            // Don't reject (would surface as uncaught); stay pending so the
            // page navigates away before any call-site sees an error.
          }
        });
      });
    }

    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);     // null = unknown, false = not authed, object = authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { timeout: 10000 });
      setUser(data);
      return data;
    } catch (err) {
      // Try refresh once before giving up
      if (err.response?.status === 401) {
        try {
          await axios.post(`${BACKEND_URL}/api/auth/refresh`, {});
          const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { timeout: 10000 });
          setUser(data);
          return data;
        } catch {
          setUser(false);
          return null;
        }
      }
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const formatErr = (detail) => {
    if (!detail) return "Algo salió mal. Intenta de nuevo.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
    }
    if (detail?.msg) return detail.msg;
    return String(detail);
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password }
      );
      setUser(data);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatErr(err.response?.data?.detail) };
    }
  };

  const register = async (email, password, name = "", turnstileToken = "") => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name, turnstile_token: turnstileToken }
      );
      setUser(data);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatErr(err.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {});
    } catch {
      // ignore
    }
    setUser(false);
  };

  // Light helper: re-fetch the user to refresh consultas_remaining
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};

export default AuthContext;
